import { Skeleton } from "antd-mobile";
import { useCallback } from "react";
import useSWR from "swr";

interface SvgRendererProps {
    url: string;
    style?: React.CSSProperties;
    className?: string;
}

const SvgRenderer: React.FC<SvgRendererProps> = ({ url, className, style }) => {
    const fetcher = useCallback(([_, url]: [string, string]) => {
        if (url == null || url.length === 0) return;

        return fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch SVG: ${response.statusText}`
                    );
                }

                return response.text();
            })
            .then((svgString) => {
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(
                    svgString,
                    "image/svg+xml"
                );

                // Modify width and height on the <svg> element
                const svgElement = svgDoc.querySelector("svg");
                if (svgElement) {
                    svgElement.setAttribute("width", "1em");
                    svgElement.setAttribute("height", "1em");
                }

                // Modify fill attributes, excluding the <svg> element
                const elementsWithFill = svgDoc.querySelectorAll("*[fill]");
                elementsWithFill.forEach((element) => {
                    if (element.tagName.toLowerCase() !== "svg") {
                        element.setAttribute("fill", "currentColor");
                    }
                });

                // Serialize the modified SVG back to a string
                const updatedSvgString = new XMLSerializer().serializeToString(
                    svgDoc
                );
                return updatedSvgString;
            });
    }, []);

    const { data: svgContent, isLoading } = useSWR(
        ["SvgRenderer", url],
        fetcher,
        {
            keepPreviousData: true,
            revalidateOnFocus: false,
            revalidateIfStale: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        }
    );

    if (isLoading) {
        return <Skeleton className={className} />;
    }

    if (svgContent && svgContent.length > 0) {
        return (
            <div
                className={className}
                style={style}
                dangerouslySetInnerHTML={{ __html: svgContent }}
            />
        );
    }

    return null;
};

export default SvgRenderer;
